"use client";

import { useState } from "react";
import { Modal } from "@/components/ui/modal";
import Button from "@/components/ui/button/Button";
import { useLocations } from "@/hooks/data/useLocations";
import { useProductLocationPricing, useSetProductLocationPricing } from "@/hooks/data/useLocations";
import { toast } from "@/ui/Toast";
import { userMessage } from "@/api/errors";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  productId: string | null;
  productName?: string;
  catalogPrice: number;
}

/**
 * Per-location price/shipping/delivery overrides for one product — a real editable table,
 * not EditModal's single-record form, since this is one row per active location. Blank
 * fields mean "use the location/product default"; saving one row is a real PUT per location
 * (LocationsController.SetProductPricing), not a bulk save button.
 */
export default function LocationPricingModal({ isOpen, onClose, productId, productName, catalogPrice }: Props) {
  const { data: locations = [] } = useLocations();
  const { data: pricing = [], isLoading } = useProductLocationPricing(productId);
  const setPricing = useSetProductLocationPricing();
  const [savingId, setSavingId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { basePrice: string; shippingCost: string; freeShippingThreshold: string; deliveryTimeEstimate: string }>>({});

  const rowFor = (locationId: string) => {
    const saved = pricing.find((p) => p.locationId === locationId);
    const draft = drafts[locationId];
    return {
      basePrice: draft?.basePrice ?? (saved?.basePrice != null ? String(saved.basePrice) : ""),
      shippingCost: draft?.shippingCost ?? (saved?.shippingCost != null ? String(saved.shippingCost) : ""),
      freeShippingThreshold: draft?.freeShippingThreshold ?? (saved?.freeShippingThreshold != null ? String(saved.freeShippingThreshold) : ""),
      deliveryTimeEstimate: draft?.deliveryTimeEstimate ?? saved?.deliveryTimeEstimate ?? "",
      effective: saved,
    };
  };

  const setDraft = (locationId: string, field: string, value: string) =>
    setDrafts((prev) => ({ ...prev, [locationId]: { ...rowFor(locationId), ...prev[locationId], [field]: value } }));

  const save = async (locationId: string) => {
    if (!productId) return;
    const row = rowFor(locationId);
    setSavingId(locationId);
    try {
      await setPricing.mutateAsync({
        productId,
        body: {
          locationId,
          basePrice: row.basePrice === "" ? null : Number(row.basePrice),
          shippingCost: row.shippingCost === "" ? null : Number(row.shippingCost),
          freeShippingThreshold: row.freeShippingThreshold === "" ? null : Number(row.freeShippingThreshold),
          deliveryTimeEstimate: row.deliveryTimeEstimate || null,
        },
      });
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[locationId];
        return next;
      });
      toast.success("Pricing saved");
    } catch (err) {
      toast.error(userMessage(err));
    } finally {
      setSavingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-4xl">
      <div className="relative w-full max-w-4xl overflow-hidden rounded-2xl bg-white p-6 dark:bg-gray-900">
        <div className="mb-4">
          <h4 className="text-xl font-semibold text-gray-800 dark:text-white/90">Location Pricing</h4>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {productName} — catalog price ${catalogPrice.toFixed(2)}. Blank fields use the location's own default (or the catalog price).
          </p>
        </div>

        {isLoading ? (
          <p className="py-6 text-center text-sm text-gray-500">Loading…</p>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="sticky top-0 bg-white text-xs uppercase text-gray-500 dark:bg-gray-900 dark:text-gray-400">
                <tr>
                  <th className="py-2 pr-2">Location</th>
                  <th className="px-2 py-2">Base Price</th>
                  <th className="px-2 py-2">Shipping</th>
                  <th className="px-2 py-2">Free Over</th>
                  <th className="px-2 py-2">Delivery</th>
                  <th className="py-2 pl-2"></th>
                </tr>
              </thead>
              <tbody>
                {locations.map((l) => {
                  const row = rowFor(l.id);
                  const dirty = !!drafts[l.id];
                  return (
                    <tr key={l.id} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="py-2 pr-2 font-medium text-gray-800 dark:text-gray-100">{l.code}</td>
                      <td className="px-2 py-2">
                        <input
                          value={row.basePrice}
                          onChange={(e) => setDraft(l.id, "basePrice", e.target.value)}
                          placeholder={`$${catalogPrice.toFixed(2)}`}
                          type="number"
                          className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          value={row.shippingCost}
                          onChange={(e) => setDraft(l.id, "shippingCost", e.target.value)}
                          placeholder={`$${l.defaultShippingCost.toFixed(2)}`}
                          type="number"
                          className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          value={row.freeShippingThreshold}
                          onChange={(e) => setDraft(l.id, "freeShippingThreshold", e.target.value)}
                          placeholder={l.defaultFreeShippingThreshold != null ? `$${l.defaultFreeShippingThreshold.toFixed(2)}` : "none"}
                          type="number"
                          className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          value={row.deliveryTimeEstimate}
                          onChange={(e) => setDraft(l.id, "deliveryTimeEstimate", e.target.value)}
                          placeholder={l.deliveryTimeEstimate}
                          className="w-28 rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800"
                        />
                      </td>
                      <td className="py-2 pl-2 text-right">
                        <button
                          onClick={() => save(l.id)}
                          disabled={!dirty || savingId === l.id}
                          className="rounded-md bg-blue-600 px-2 py-1 text-xs text-white disabled:opacity-30"
                        >
                          {savingId === l.id ? "…" : "Save"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  );
}
